<template>
  <div class="animated fadeIn page-shell">
    <div class="page-main">
      <header-padrao class="mb-2" icon="fa fa-balance-scale" titulo="CONCILIAÇÃO BANCÁRIA">
        <template #subtitulo>
          <div class="d-flex align-items-center flex-wrap">
            <span class="mr-2">
              <i class="fa fa-key mr-1 text-info"></i>
              ID: <strong class="text-white">{{registro.ID || 'Novo'}}</strong>
            </span>
            <span v-if="registro.STATUS" class="mr-2">
              <b-badge :variant="getVariantStatus(registro.STATUS)" pill>
                {{ getLabelStatus(registro.STATUS) }}
              </b-badge>
            </span>
          </div>
        </template>

        <template #acoes>
          <b-button variant="link" class="btn-glass btn-glass-success px-4 font-weight-bold" @click.stop.prevent="gravar()">
            <i class="fa fa-save mr-1"></i> {{ registro.ID ? 'SALVAR' : 'GRAVAR' }}
          </b-button>
          <b-button variant="light" pill class="ml-2 font-weight-bold shadow-sm px-4" @click.stop.prevent="voltar()">
            <i class="fa fa-arrow-left mr-1"></i> Voltar
          </b-button>
        </template>
      </header-padrao>

      <b-row class="px-2">
        <b-col sm="12">
          <!-- Card 1: Dados da Conciliação -->
          <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <h5 class="mb-3">
              <i class="fa fa-info-circle mr-2 text-primary"></i>
              Dados da Conciliação
            </h5>
            <b-row>
              <b-col md="4">
                <label class="font-weight-bold">Conta Bancária *</label>
                <ContaDropDown v-model="registro.CONTA_ID" :disabled="!!registro.ID" />
              </b-col>
              <b-col md="3">
                <label class="font-weight-bold">Data Inicial *</label>
                <CecInputData v-model="registro.DATA_INICIAL" :disabled="periodoBloqueado" />
              </b-col>
              <b-col md="3">
                <label class="font-weight-bold">Data Final *</label>
                <CecInputData v-model="registro.DATA_FINAL" :disabled="periodoBloqueado" />
              </b-col>
              <b-col md="2">
                <label class="font-weight-bold">Status</label>
                <b-form-input :value="getLabelStatus(registro.STATUS)" disabled class="shadow-xs"></b-form-input>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col md="12">
                <label class="font-weight-bold">Observação</label>
                <b-form-textarea v-model="registro.OBSERVACAO" rows="2" class="shadow-xs"></b-form-textarea>
              </b-col>
            </b-row>
          </b-card>

          <!-- Card 2: Importação de Extrato -->
          <b-card v-if="registro.ID && registro.STATUS !== statusConciliacao.CANCELADA" class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <h5 class="mb-3">
              <i class="fa fa-upload mr-2 text-success"></i>
              Importação de Extrato
            </h5>
            <b-row>
              <b-col md="8">
                <label class="font-weight-bold">Arquivo do Extrato (CSV, OFX, TXT)</label>
                <b-form-file 
                  v-model="arquivoExtrato" 
                  placeholder="Selecione o arquivo do extrato..." 
                  accept=".csv,.ofx,.txt"
                  class="shadow-xs">
                </b-form-file>
              </b-col>
              <b-col md="4" class="d-flex align-items-end">
                <b-button variant="primary" pill class="px-4 font-weight-bold" @click="importarExtrato" :disabled="!arquivoExtrato">
                  <i class="fa fa-cloud-upload mr-1"></i> IMPORTAR
                </b-button>
              </b-col>
            </b-row>
            <b-alert v-if="registro.NOME_ARQUIVO" show variant="info" class="mt-3 mb-0">
              <i class="fa fa-info-circle mr-2"></i>
              <strong>Arquivo Importado:</strong> {{ registro.NOME_ARQUIVO }} 
              ({{ registro.TRANSACOES || 0 }} transações)
            </b-alert>
          </b-card>

          <!-- Card 3: Itens do Extrato -->
          <b-card v-if="registro.ID && itensConciliacao.length > 0" class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0">
                <i class="fa fa-list mr-2 text-info"></i>
                Itens do Extrato
              </h5>
              <div>
                <b-badge variant="secondary" pill class="mr-2">
                  Total: {{ itensConciliacao.length }}
                </b-badge>
                <b-badge variant="success" pill class="mr-2">
                  Conciliados: {{ itensConciliados }}
                </b-badge>
                <b-badge variant="warning" pill>
                  Pendentes: {{ itensPendentes }}
                </b-badge>
              </div>
            </div>
            
            <b-table 
              :items="itensConciliacao" 
              :fields="colunasItens"
              hover
              small
              responsive
              class="mb-0"
              @row-clicked="selecionarItem"
              :tbody-tr-class="rowClassItem"
              :tbody-tr-attr="rowAttrsItem">
              
              <template #cell(DATA_MOVIMENTO)="row">
                {{ formatarDataBR(row.item.DATA_MOVIMENTO) }}
              </template>

              <template #cell(TIPO_MOV)="row">
                <b-badge :variant="row.item.TIPO_MOV === 'C' ? 'success' : 'danger'" pill>
                  {{ row.item.TIPO_MOV === 'C' ? 'Crédito' : 'Débito' }}
                </b-badge>
              </template>

              <template #cell(VALOR)="row">
                <span class="font-weight-bold" :class="row.item.TIPO_MOV === 'C' ? 'text-success' : 'text-danger'">
                  {{ formatarMoeda(row.item.VALOR) }}
                </span>
              </template>

              <template #cell(STATUS)="row">
                <b-badge :variant="getVariantStatusItem(row.item.STATUS)" pill>
                  {{ getLabelStatusItem(row.item.STATUS) }}
                </b-badge>
              </template>

              <template #cell(VALOR_CONCILIADO)="row">
                <span class="text-success font-weight-bold">{{ formatarMoeda(row.item.VALOR_CONCILIADO) }}</span>
              </template>

              <template #cell(VALOR_PENDENTE)="row">
                <span class="text-warning font-weight-bold">{{ formatarMoeda(row.item.VALOR_PENDENTE) }}</span>
              </template>

              <template #cell(QTD_VINCULOS)="row">
                <b-badge variant="info" pill>{{ row.item.QTD_VINCULOS || 0 }}</b-badge>
              </template>
            </b-table>
          </b-card>

        </b-col>
      </b-row>

      <!-- Modal de Vinculação -->
      <b-modal 
        ref="modalVinculacao" 
        id="modal-vinculacao" 
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        centered
        size="xl"
        @shown="onModalShown"
        @hidden="onModalHidden">
        
        <!-- Cabeçalho Premium -->
        <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" 
             style="background: linear-gradient(135deg, #20a8d8 0%, #1783ae 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-link text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Vincular Lançamento</h5>
            <small class="text-white opacity-75">Concilie o item do extrato com lançamentos</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="fecharModal">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <!-- Barra de Informações do Item -->
        <div class="modal-info-bar mb-4 bg-light border shadow-sm p-3 rounded mx-3 mt-3" v-if="itemSelecionado">
          <b-row align-v="center">
            <b-col md="4">
              <small class="modal-label-premium">Data do Movimento</small>
              <h5 class="mb-0 font-weight-bold text-dark">{{ formatarDataBR(itemSelecionado.DATA_MOVIMENTO) }}</h5>
              <small class="text-muted font-weight-bold">{{ itemSelecionado.HISTORICO }}</small>
            </b-col>
            <b-col md="3" class="border-left">
              <small class="modal-label-premium">Documento</small>
              <h5 class="mb-0 font-weight-bold text-primary">{{ itemSelecionado.DOCUMENTO || 'N/A' }}</h5>
              <b-badge :variant="itemSelecionado.TIPO_MOV === 'C' ? 'success' : 'danger'" pill>
                {{ itemSelecionado.TIPO_MOV === 'C' ? 'Crédito' : 'Débito' }}
              </b-badge>
            </b-col>
            <b-col md="3" class="border-left">
              <small class="modal-label-premium">Valor do Item</small>
              <h5 class="mb-0 font-weight-bold" :class="itemSelecionado.TIPO_MOV === 'C' ? 'text-success' : 'text-danger'">
                {{ formatarMoeda(Math.abs(itemSelecionado.VALOR)) }}
              </h5>
            </b-col>
            <b-col md="2" class="border-left text-right">
              <small class="modal-label-premium">Valor Pendente</small>
              <h5 class="mb-0 font-weight-bold text-warning">
                {{ formatarMoeda(itemSelecionado.VALOR_PENDENTE) }}
              </h5>
            </b-col>
          </b-row>
          <b-row align-v="center" class="mt-3">
            <b-col md="4">
              <small class="modal-label-premium">Valor Conciliado</small>
              <h6 class="mb-0 font-weight-bold text-success">{{ formatarMoeda(itemSelecionado.VALOR_CONCILIADO || 0) }}</h6>
            </b-col>
            <b-col md="4" class="border-left">
              <small class="modal-label-premium">Tolerância da Conta</small>
              <h6 class="mb-0 font-weight-bold text-info">{{ formatarMoeda(toleranciaContaAtual) }}</h6>
            </b-col>
            <b-col md="4" class="border-left text-right">
              <small class="modal-label-premium">Diferença a Ajustar</small>
              <h6 class="mb-0 font-weight-bold" :class="conclusaoManualDentroTolerancia ? 'text-info' : 'text-danger'">
                {{ formatarMoeda(diferencaConclusaoManual) }}
              </h6>
            </b-col>
          </b-row>
        </div>

        <div v-if="itemSelecionado && itemSelecionado.STATUS === 'CONCILIADO_MANUAL'" class="mx-3 mb-2">
          <b-alert show variant="primary" class="mb-0">
            <div class="font-weight-bold mb-1">Item encerrado manualmente</div>
            <div><strong>Motivo:</strong> {{ itemSelecionado.MOTIVO_AJUSTE || 'Não informado' }}</div>
            <div><strong>Valor do ajuste:</strong> {{ formatarMoeda(itemSelecionado.VALOR_AJUSTE || 0) }}</div>
            <div><strong>Usuário do ajuste:</strong> {{ itemSelecionado.USUARIO_AJUSTE || '-' }}</div>
            <div><strong>Data do ajuste:</strong> {{ formatarDataHoraBR(itemSelecionado.DATA_AJUSTE) }}</div>
          </b-alert>
        </div>

        <div v-if="podeExibirAcaoConclusaoManual" class="mx-3 mb-3">
          <b-button
            variant="outline-primary"
            pill
            class="font-weight-bold"
            @click="mostrarConclusaoManual = !mostrarConclusaoManual">
            <i class="fa fa-flag-checkered mr-1"></i>
            {{ mostrarConclusaoManual ? 'Ocultar conclusão manual' : 'Concluir manualmente' }}
          </b-button>

          <b-card v-if="mostrarConclusaoManual" class="mt-3 border shadow-xs">
            <b-alert :show="true" :variant="conclusaoManualDentroTolerancia ? 'warning' : 'danger'" class="mb-3">
              {{ mensagemConclusaoManual }}
            </b-alert>

            <b-row>
              <b-col md="3">
                <small class="modal-label-premium">Valor Conciliado</small>
                <h6 class="mb-0 font-weight-bold text-success">{{ formatarMoeda(itemSelecionado.VALOR_CONCILIADO || 0) }}</h6>
              </b-col>
              <b-col md="3">
                <small class="modal-label-premium">Valor Pendente</small>
                <h6 class="mb-0 font-weight-bold text-warning">{{ formatarMoeda(itemSelecionado.VALOR_PENDENTE || 0) }}</h6>
              </b-col>
              <b-col md="3">
                <small class="modal-label-premium">Tolerância da Conta</small>
                <h6 class="mb-0 font-weight-bold text-info">{{ formatarMoeda(toleranciaContaAtual) }}</h6>
              </b-col>
              <b-col md="3">
                <small class="modal-label-premium">Diferença a Ajustar</small>
                <h6 class="mb-0 font-weight-bold" :class="conclusaoManualDentroTolerancia ? 'text-info' : 'text-danger'">
                  {{ formatarMoeda(diferencaConclusaoManual) }}
                </h6>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="12">
                <label class="font-weight-bold">Motivo do ajuste *</label>
                <b-form-textarea
                  v-model="motivoConclusaoManual"
                  rows="3"
                  maxlength="500"
                  placeholder="Descreva o motivo para encerrar o item manualmente">
                </b-form-textarea>
              </b-col>
            </b-row>

            <div class="text-right mt-3">
              <b-button variant="primary" pill class="px-4 font-weight-bold" @click="confirmarConclusaoManual">
                <i class="fa fa-check mr-1"></i> Confirmar conclusão manual
              </b-button>
            </div>
          </b-card>
        </div>

        <!-- Corpo do Modal com Abas -->
        <div class="px-3 pb-3" style="max-height: 600px; overflow-y: auto;">
          <b-tabs content-class="mt-3">
            <!-- Aba Lançamentos -->
            <b-tab title="Lançamentos" active>
              <template #title>
                <i class="fa fa-dollar mr-1"></i> Lançamentos
              </template>
              
              <div class="mb-3 d-flex align-items-center justify-content-between flex-wrap">
                <b-button variant="primary" size="sm" pill @click="carregarLancamentos">
                  <i class="fa fa-refresh mr-1"></i> Carregar Lançamentos
                </b-button>
                <b-button
                  v-if="podeGerarLancamento"
                  variant="success"
                  size="sm"
                  pill
                  class="mt-2 mt-sm-0"
                  @click="abrirModalGerarLancamento">
                  <i class="fa fa-plus-circle mr-1"></i> Gerar lançamento
                </b-button>
              </div>

              <b-table 
                :items="CANDIDATOS_RBANMOV1" 
                :fields="colunasLancamentos"
                hover
                small
                responsive
                show-empty
                empty-text="Nenhum lançamento encontrado"
                selectable
                select-mode="multi"
                @row-selected="onLancamentosSelecionados"
                class="modal-table-premium">
                
                <template #cell(selected)="{ rowSelected }">
                  <template v-if="rowSelected">
                    <i class="fa fa-check-square-o text-success"></i>
                  </template>
                  <template v-else>
                    <i class="fa fa-square-o"></i>
                  </template>
                </template>

                <template #cell(DATA_MOVIMENTO)="row">
                  {{ formatarDataBR(row.item.DATA_MOVIMENTO) }}
                </template>

                <template #cell(VALOR)="row">
                  <span class="font-weight-bold">{{ formatarMoeda(row.item.VALOR) }}</span>
                </template>

                <template #cell(VALOR_DISPONIVEL)="row">
                  <span class="font-weight-bold text-success">{{ formatarMoeda(row.item.VALOR_DISPONIVEL) }}</span>
                </template>

                <template #cell(VALOR_VINCULAR)="row">
                  <vue-autonumeric
                    v-if="lancamentosSelecionadosIds.includes(row.item.ID)"
                    v-model="valoresVinculoLancamentos[row.item.ID]"
                    :options="formatoValorVincular"
                    class="form-control form-control-sm text-right"
                    @blur.native="ajustarValorVincular(row.item)"
                  ></vue-autonumeric>
                  <span v-else class="text-muted">-</span>
                </template>
              </b-table>

              <div v-if="lancamentosSelecionados.length > 0" class="mt-3 p-3 bg-light rounded border shadow-xs">
                <b-row>
                  <b-col md="4">
                    <small class="modal-label-premium">Valor Pendente</small>
                    <h6 class="mb-0 font-weight-bold text-dark">{{ formatarMoeda(itemSelecionado.VALOR_PENDENTE || 0) }}</h6>
                  </b-col>
                  <b-col md="4">
                    <small class="modal-label-premium">Soma Selecionada</small>
                    <h6 class="mb-0 font-weight-bold" :class="diferencaLancamentos <= 0.009 ? 'text-success' : 'text-danger'">
                      {{ formatarMoeda(somaLancamentos) }}
                    </h6>
                  </b-col>
                  <b-col md="4">
                    <small class="modal-label-premium">Diferença</small>
                    <h6 class="mb-0 font-weight-bold" :class="diferencaLancamentos <= 0.009 ? 'text-success' : 'text-danger'">
                      {{ formatarMoeda(diferencaLancamentos) }}
                    </h6>
                  </b-col>
                </b-row>
                <b-row class="mt-3">
                  <b-col md="12" class="text-right">
                    <b-button 
                      variant="success" 
                      pill
                      class="px-5 font-weight-bold"
                      @click="vincularLancamentos" 
                      :disabled="diferencaLancamentos > 0.009">
                      <i class="fa fa-check-circle mr-1"></i> CONFIRMAR VÍNCULO
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </b-tab>

            <!-- Aba Vínculos -->
            <b-tab title="Vínculos">
              <template #title>
                <i class="fa fa-chain mr-1"></i> Vínculos
              </template>
              
              <div class="mb-3">
                <b-button variant="primary" size="sm" pill @click="carregarVinculos">
                  <i class="fa fa-refresh mr-1"></i> Atualizar Vínculos
                </b-button>
              </div>

              <b-table 
                :items="VINCULOS_ITEM_SELECIONADO" 
                :fields="colunasVinculos"
                hover
                small
                responsive
                show-empty
                empty-text="Nenhum vínculo encontrado"
                class="mb-0 modal-table-premium">
                
                <template #cell(DATA_CADASTRO)="row">
                  {{ formatarDataBR(row.item.DATA_CADASTRO) }}
                </template>

                <template #cell(VALOR_VINCULADO)="row">
                  <span class="font-weight-bold text-success">{{ formatarMoeda(row.item.VALOR_VINCULADO) }}</span>
                </template>

                <template #cell(actions)="row">
                  <b-button
                    v-if="itemSelecionado && itemSelecionado.STATUS !== 'CONCILIADO_MANUAL'"
                    size="sm"
                    variant="danger"
                    pill
                    @click="desfazerVinculo(row.item)">
                    <i class="fa fa-trash mr-1"></i> Desfazer
                  </b-button>
                  <b-badge v-else variant="secondary" pill>
                    Bloqueado
                  </b-badge>
                </template>
              </b-table>
            </b-tab>
          </b-tabs>
        </div>

        <!-- Rodapé de Ações -->
        <div class="modal-footer-premium p-3 bg-light border-top d-flex justify-content-end">
          <b-button variant="secondary" pill class="px-4 font-weight-bold" @click="fecharModal">
            <i class="fa fa-times mr-1"></i> FECHAR
          </b-button>
        </div>
      </b-modal>

      <b-modal
        ref="modalGerarLancamento"
        id="modal-gerar-lancamento-conciliacao"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg"
        size="lg"
        no-close-on-esc
        no-close-on-backdrop
        @hidden="onModalGerarLancamentoHidden">

        <b-overlay
          :show="lancamentoGerado.parametros.overlay"
          no-wrap
          z-index="1100"
          spinner-variant="primary"
          spinner-type="border">
          <template #overlay>
            <div class="text-center">
              <i class="fa fa-spinner fa-pulse fa-3x text-primary"></i>
              <p class="font-weight-bold text-primary mt-2 mb-0">Gerando lançamento...</p>
            </div>
          </template>
        </b-overlay>

        <div class="modal-header-premium p-3 d-flex align-items-center bg-dark"
             style="background: linear-gradient(135deg, #20a8d8 0%, #1783ae 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-calculator text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Gerar Lançamento no Caixa</h5>
            <small class="text-white opacity-75">Criação oficial na RBANMOV1 a partir do item do extrato</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="fecharModalGerarLancamento">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="p-4 bg-white">
          <div class="modal-info-bar mb-4 shadow-xs border" v-if="itemSelecionado">
            <b-row align-v="center">
              <b-col md="5">
                <small class="modal-label-premium d-block">Conta Bancária</small>
                <span class="font-weight-bold text-dark">
                  <i class="fa fa-university mr-2 text-muted"></i>{{ registro.CONTA_NOME || registro.CONTA_ID }}
                </span>
              </b-col>
              <b-col md="3" class="border-left border-right text-center">
                <small class="modal-label-premium d-block">Tipo Movimento</small>
                <b-badge :variant="lancamentoGerado.registro.TIPO_MOV === 'DEBITO' ? 'danger' : 'success'" pill>
                  {{ lancamentoGerado.registro.TIPO_MOV }}
                </b-badge>
              </b-col>
              <b-col md="4" class="text-right">
                <small class="modal-label-premium d-block">Data do Movimento</small>
                <span class="font-weight-bold text-dark">
                  <i class="fa fa-calendar mr-2 text-muted"></i>{{ formatarDataBR(lancamentoGerado.registro.DT_MOV) }}
                </span>
              </b-col>
            </b-row>
          </div>

          <div class="modal-section-title">
            <i class="fa fa-file-text-o mr-2 text-primary"></i> Dados do Lançamento
          </div>

          <b-row class="modal-field-group">
            <b-col md="6">
              <label class="modal-label-premium" for="documentoGerado">Nº Documento</label>
              <b-form-input
                id="documentoGerado"
                ref="documentoGerado"
                :value="lancamentoGerado.registro.DOCUMENTO"
                readonly
                class="rounded-pill shadow-xs bg-light">
              </b-form-input>
            </b-col>
            <b-col md="6">
              <label class="modal-label-premium text-right d-block" for="valorGerado">Valor do Lançamento</label>
              <b-form-input
                id="valorGerado"
                :value="formatarMoeda(lancamentoGerado.registro.VL_MOV || 0)"
                readonly
                class="rounded-pill shadow-xs bg-light text-right">
              </b-form-input>
            </b-col>
          </b-row>

          <b-row class="modal-field-group">
            <b-col md="12">
              <label class="modal-label-premium">Histórico do Extrato</label>
              <b-form-textarea
                :value="lancamentoGerado.registro.OBS"
                rows="2"
                no-resize
                readonly
                class="shadow-xs bg-light">
              </b-form-textarea>
            </b-col>
          </b-row>

          <b-row class="modal-field-group">
            <b-col md="12">
              <label class="modal-label-premium">Histórico Financeiro *</label>
              <historico-caixa-dropdown
                v-model="lancamentoGerado.registro.COD_PLANO"
                :state="validou_gerar_lancamento_historico"
                placeholder="Selecione o plano de contas..."
                class="shadow-xs"
                :tipo="lancamentoGerado.registro.TIPO_MOV || 'DEBITO'" />
            </b-col>
          </b-row>

          <b-row class="modal-field-group">
            <b-col md="12">
              <label class="modal-label-premium">Cliente ou Fornecedor</label>
              <cliente-pesquisa
                :key="gerarLancamentoClienteKey"
                @retornaId="lancamentoGerado.registro.CLIENTE_ID = $event"
                :idInicial="lancamentoGerado.registro.CLIENTE_ID"
                :classe-required="!!lancamentoGerado.registro.CLIENTE_ID"
                place="Opcional: Pesquise pelo nome..."
                labelBotao=""
                ref="clientePesquisaGerarLancamento"
                idModal="clientePesquisaGerarLancamento"
                :layoutLinear="true"
                :tamanhoColunaLabel="'3'"
                :tamanhoColunaAutocomplete="'9'"
                class="shadow-xs" />
            </b-col>
          </b-row>

          <b-row class="modal-field-group">
            <b-col md="12">
              <label class="modal-label-premium">Centro de Custo *</label>
              <centro-custo-drop-down
                v-model="lancamentoGerado.registro.COD_CENTRO1"
                :state="validou_gerar_lancamento_centro"
                placeholder="Selecione a unidade..." />
            </b-col>
          </b-row>

          <b-row class="modal-field-group">
            <b-col md="12">
              <label class="modal-label-premium" for="formaGerada">Forma Pagto. *</label>
              <b-form-select
                id="formaGerada"
                v-model="lancamentoGerado.registro.FORMA"
                :state="validou_gerar_lancamento_forma"
                :options="lancamentoGerado.tipoPagamento.items"
                class="rounded-pill shadow-xs">
                <template slot="first">
                  <option :value="null">-- Selecione --</option>
                </template>
              </b-form-select>
            </b-col>
          </b-row>
        </div>

        <div class="modal-footer-premium p-3 bg-light d-flex justify-content-between">
          <b-button
            :disabled="lancamentoGerado.parametros.overlay"
            variant="secondary"
            pill
            class="px-4 font-weight-bold"
            @click="fecharModalGerarLancamento">
            FECHAR
          </b-button>
          <b-button
            :disabled="lancamentoGerado.parametros.overlay"
            variant="success"
            pill
            class="px-4 font-weight-bold shadow-sm"
            @click="confirmarGeracaoLancamento">
            <i class="fa fa-save mr-1"></i> GERAR E VINCULAR
          </b-button>
        </div>
      </b-modal>

    </div>
  </div>
</template>

<script>
import HeaderPadrao from '@/components/Cec/HeaderPadrao.vue'
import CecInputData from '@/components/Cec/CecInputData.vue'
import ContaDropDown from '@/views/componentes/dropdown/ContaDropDown.vue'
import ClientePesquisa from '../../pesquisa/ClientePesquisa.vue'
import HistoricoCaixaDropdown from '../../componentes/dropdown/HistoricoCaixaDropdown.vue'
import CentroCustoDropDown from '../../componentes/dropdown/CentroCustoDropDown.vue'
import VueAutonumeric from 'vue-autonumeric'
import { mapGetters } from 'vuex'
import { STATUS_CONCILIACAO, getLabelStatusConciliacao, getVariantStatusConciliacao } from '@/utils/conciliacaoStatus'

export default {
  components: { 
    HeaderPadrao,
    CecInputData,
    ContaDropDown,
    ClientePesquisa,
    HistoricoCaixaDropdown,
    CentroCustoDropDown,
    VueAutonumeric
  },
  
  data() {
    return {
      registro: {
        ID: null,
        CONTA_ID: null,
        DATA_INICIAL: '',
        DATA_FINAL: '',
        STATUS: STATUS_CONCILIACAO.PENDENTE,
        OBSERVACAO: ''
      },
      statusConciliacao: STATUS_CONCILIACAO,
      formatoValorVincular: {
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        decimalPlaces: '2',
        roundingMethod: 'U',
        minimumValue: '0'
      },
      arquivoExtrato: null,
      itemSelecionado: null,
      abrindoModalItem: false,
      mostrarConclusaoManual: false,
      motivoConclusaoManual: '',
      gerarLancamentoClienteKey: 0,
      lancamentosSelecionados: [],
      valoresVinculoLancamentos: {},
      lancamentoGerado: {
        parametros: {
          overlay: false,
          submmited: false
        },
        tipoPagamento: {
          items: []
        },
        registro: {
          CONTADOR: null,
          SEQUENCIA: null,
          COD_CONTA: null,
          DOCUMENTO: null,
          COD_BANCO: null,
          COD_PLANO: null,
          OCORRENCIA: null,
          TIPO_MOV: null,
          DT_MOV: null,
          VL_MOV: 0,
          FORMA: null,
          CONTROL_CHEQUE: null,
          MARCA: null,
          OBS: null,
          COD_CENTRO1: null,
          HORA: null,
          PARCELA: null,
          CLIENTE_ID: null
        }
      },
      colunasItens: [
        { key: 'SEQUENCIA', label: '#', class: 'text-center', thStyle: { width: '60px' } },
        { key: 'DATA_MOVIMENTO', label: 'Data', thStyle: { width: '110px' } },
        { key: 'DOCUMENTO', label: 'Documento', thStyle: { width: '150px' } },
        { key: 'HISTORICO', label: 'Histórico' },
        { key: 'TIPO_MOV', label: 'Tipo', class: 'text-center', thStyle: { width: '90px' } },
        { key: 'VALOR', label: 'Valor', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'VALOR_CONCILIADO', label: 'Conciliado', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'VALOR_PENDENTE', label: 'Pendente', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'STATUS', label: 'Status', class: 'text-center', thStyle: { width: '140px' } },
        { key: 'QTD_VINCULOS', label: 'Vínculos', class: 'text-center', thStyle: { width: '90px' } }
      ],
      colunasLancamentos: [
        { key: 'selected', label: '', thStyle: { width: '40px' } },
        { key: 'ID', label: 'ID', thStyle: { width: '80px' } },
        { key: 'DATA_MOVIMENTO', label: 'Data', thStyle: { width: '110px' } },
        { key: 'DOCUMENTO', label: 'Documento', thStyle: { width: '150px' } },
        { key: 'HISTORICO', label: 'Histórico' },
        { key: 'VALOR', label: 'Valor', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'VALOR_DISPONIVEL', label: 'Disponível', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'VALOR_VINCULAR', label: 'Vincular', class: 'text-right', thStyle: { width: '130px' } }
      ],
      colunasVinculos: [
        { key: 'ID', label: 'ID', thStyle: { width: '80px' } },
        { key: 'TIPO_ORIGEM', label: 'Tipo', thStyle: { width: '120px' } },
        { key: 'ORIGEM_REFERENCIA', label: 'Origem', thStyle: { width: '100px' } },
        { key: 'RBANMOV1_DOCUMENTO', label: 'Documento', thStyle: { width: '150px' } },
        { key: 'RBANMOV1_HISTORICO', label: 'Histórico' },
        { key: 'VALOR_VINCULADO', label: 'Valor', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'USUARIO_CADASTRO', label: 'Usuário', thStyle: { width: '120px' } },
        { key: 'DATA_CADASTRO', label: 'Data', thStyle: { width: '110px' } },
        { key: 'actions', label: 'Ações', class: 'text-center', thStyle: { width: '100px' } }
      ]
    }
  },
  
  computed: {
    isDark() { return this.$store.getters['tema/IS_DARK'] },
    ...mapGetters('conciliacaoBancaria', [
      'ITENS_CONCILIACAO',
      'CANDIDATOS_RBANMOV1',
      'VINCULOS_ITEM_SELECIONADO'
    ]),
    itensConciliacao() {
      return this.ITENS_CONCILIACAO || []
    },
    itensConciliados() {
      return this.itensConciliacao.filter(i => ['CONCILIADO', 'CONCILIADO_MANUAL'].includes(i.STATUS)).length
    },
    itensPendentes() {
      return this.itensConciliacao.filter(i => ['PENDENTE', 'CONCILIADO_PARCIAL'].includes(i.STATUS)).length
    },
    lancamentosSelecionadosIds() {
      return this.lancamentosSelecionados.map(item => item.ID)
    },
    somaLancamentos() {
      return this.lancamentosSelecionados.reduce((sum, item) => {
        const valor = Number(this.valoresVinculoLancamentos[item.ID] || 0)
        return sum + (Number.isFinite(valor) ? valor : 0)
      }, 0)
    },
    diferencaLancamentos() {
      if (!this.itemSelecionado) return 0
      return Math.abs((this.itemSelecionado.VALOR_PENDENTE || 0) - this.somaLancamentos)
    },
    toleranciaContaAtual() {
      const valor = Number(this.registro.TOLERANCIA_CONCILIACAO || 0)
      return Number.isFinite(valor) ? valor : 0
    },
    diferencaConclusaoManual() {
      if (!this.itemSelecionado) return 0
      const valor = Number(this.itemSelecionado.VALOR_PENDENTE || 0)
      return Number.isFinite(valor) ? Math.max(0, valor) : 0
    },
    conclusaoManualDentroTolerancia() {
      return this.diferencaConclusaoManual <= (this.toleranciaContaAtual + 0.009)
    },
    mensagemConclusaoManual() {
      if (this.conclusaoManualDentroTolerancia) {
        return 'A diferença está dentro da tolerância da conta. O item poderá ser concluído manualmente.'
      }
      return 'A diferença está acima da tolerância da conta. A conclusão manual ficará registrada como ajuste.'
    },
    podeExibirAcaoConclusaoManual() {
      if (!this.itemSelecionado) return false
      const status = String(this.itemSelecionado.STATUS || '')
      const pendente = Number(this.itemSelecionado.VALOR_PENDENTE || 0)
      return !['CONCILIADO', 'CONCILIADO_MANUAL'].includes(status) && pendente > 0
    },
    podeGerarLancamento() {
      if (!this.itemSelecionado) return false
      const status = String(this.itemSelecionado.STATUS || '')
      const pendente = Number(this.itemSelecionado.VALOR_PENDENTE || 0)
      return status !== 'CONCILIADO_MANUAL' && pendente > 0.009
    },
    validouGerarLancamento() {
      return !!this.lancamentoGerado.registro.COD_PLANO &&
        !!this.lancamentoGerado.registro.FORMA &&
        !!this.lancamentoGerado.registro.COD_CENTRO1
    },
    validou_gerar_lancamento_historico() {
      const existe = !!this.lancamentoGerado.registro.COD_PLANO
      return this.lancamentoGerado.parametros.submmited ? (existe ? null : false) : null
    },
    validou_gerar_lancamento_forma() {
      const existe = !!this.lancamentoGerado.registro.FORMA
      return this.lancamentoGerado.parametros.submmited ? (existe ? null : false) : null
    },
    validou_gerar_lancamento_centro() {
      const existe = !!this.lancamentoGerado.registro.COD_CENTRO1
      return this.lancamentoGerado.parametros.submmited ? (existe ? null : false) : null
    },
    periodoBloqueado() {
      return Number(this.registro.TRANSACOES || 0) > 0 || this.itensConciliacao.length > 0
    }
  },
  
  methods: {
    voltar() { 
      this.$router.push('/movimentos/conciliacao-bancaria/listagem') 
    },
    
    async carregar(id) {
      this.$store.commit('controle/setAguardando', true)
      try {
        const response = await this.$http.get(`/api/protected/conciliacao-bancaria/getById/${id}`)
        if (Array.isArray(response.data) && response.data.length > 0) {
          this.registro = { ...this.registro, ...response.data[0] }
        }
        
        // Carregar itens
        await this.$store.dispatch('conciliacaoBancaria/GET_ITENS', id)
      } catch (err) {
        console.error('Erro ao carregar', err)
        this.$bvToast.toast('Erro ao carregar dados', { variant: 'danger' })
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },
    
    async gravar() {
      // Validações
      if (!this.registro.CONTA_ID) {
        this.$bvToast.toast('Selecione a conta bancária', { variant: 'warning' })
        return
      }
      if (!this.registro.DATA_INICIAL) {
        this.$bvToast.toast('Informe a data inicial', { variant: 'warning' })
        return
      }
      if (!this.registro.DATA_FINAL) {
        this.$bvToast.toast('Informe a data final', { variant: 'warning' })
        return
      }

      try {
        const body = {
          registro: { ...this.registro },
          login: window.localStorage.getItem('login') || 'sistema'
        }

        const isEdicao = !!this.registro.ID
        const endpoint = isEdicao
          ? '/api/protected/conciliacao-bancaria/edit'
          : '/api/protected/conciliacao-bancaria/post'
        
        const res = await this.$http.post(endpoint, body)
        
        const idSalvo = res.data?.registro?.ID || null
        
        if (idSalvo) {
          this.registro = {
            ...this.registro,
            ...(res.data?.registro || {}),
            ID: idSalvo
          }
          this.$store.commit('conciliacaoBancaria/SET_REGISTRO_SELECIONADO_ID', idSalvo)
          this.$bvToast.toast(isEdicao ? 'Conciliação atualizada com sucesso' : 'Conciliação criada com sucesso', { variant: 'success' })
          if (!isEdicao && String(this.$route.params.id || '') !== String(idSalvo)) {
            this.$router.replace(`/movimentos/conciliacao-bancaria/edit/${idSalvo}`)
          }
        }
        
        this.$store.commit('conciliacaoBancaria/SET_RECARREGAR_LISTA', true)
      } catch (err) {
        console.error('Erro ao gravar', err)
        this.$bvToast.toast(err.response?.data || 'Erro ao salvar registro', { variant: 'danger' })
      }
    },

    async importarExtrato() {
      if (!this.arquivoExtrato) {
        this.$bvToast.toast('Selecione um arquivo', { variant: 'warning' })
        return
      }

      if (!this.registro.ID) {
        this.$bvToast.toast('Salve a conciliação antes de importar o extrato', { variant: 'warning' })
        return
      }

      try {
        // Ler o arquivo como texto
        const fileContent = await this.lerArquivoTexto(this.arquivoExtrato)
        const arquivoNome = this.arquivoExtrato?.name || null
        const arquivoPath = arquivoNome ? `WEB_UPLOAD/${arquivoNome}` : null
        
        // Parsear o extrato
        const itensParsed = this.parsearExtrato(fileContent)
        
        if (itensParsed.length === 0) {
          this.$bvToast.toast('Nenhum item encontrado no arquivo', { variant: 'warning' })
          return
        }

        // Enviar para o backend
        const body = {
          conciliacaoId: this.registro.ID,
          itens: itensParsed,
          nomeArquivo: arquivoNome,
          pathArquivo: arquivoPath,
          arquivoConteudo: fileContent,
          login: window.localStorage.getItem('login') || 'sistema'
        }
        
        const res = await this.$http.post('/api/protected/conciliacao-bancaria/importar-extrato', body)
        
        this.$bvToast.toast(`${res.data.itensImportados} itens importados com sucesso`, { 
          variant: 'success',
          title: 'Sucesso'
        })
        
        // Recarregar dados
        await this.carregar(this.registro.ID)
      } catch (err) {
        console.error('Erro ao importar extrato', err)
        this.$bvToast.toast(err.response?.data || err.message || 'Erro ao importar extrato', { 
          variant: 'danger',
          title: 'Erro'
        })
      }
    },

    lerArquivoTexto(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = (e) => reject(e)
        reader.readAsText(file, 'UTF-8')
      })
    },

    parsearExtrato(conteudo) {
      const linhas = conteudo.split('\n')
      const itens = []
      
      for (let linha of linhas) {
        linha = linha.trim()
        
        // Ignorar linhas vazias
        if (!linha) continue
        
        // Split por ponto e vírgula
        const partes = linha.split(';')
        
        // Deve ter pelo menos 3 partes: data, historico, valor
        if (partes.length < 3) continue
        
        try {
          const dataStr = partes[0].trim()
          const historico = partes[1].trim()
          const valorStr = partes[2].trim()
          
          // Converter data dd/mm/aaaa para yyyy-mm-dd
          const dataMovimento = this.converterDataBR(dataStr)
          if (!dataMovimento) continue
          
          // Converter valor brasileiro (1.234,56 ou -1.234,56) para número
          const valor = this.converterValorBR(valorStr)
          if (isNaN(valor)) continue
          
          // Determinar tipo de movimento
          const tipoMov = valor >= 0 ? 'C' : 'D'
          
          itens.push({
            DATA_MOVIMENTO: dataMovimento,
            DATA_LANCAMENTO: dataMovimento,
            DOCUMENTO: '',
            HISTORICO: historico,
            TIPO_MOV: tipoMov,
            FORMA: this.inferirForma(historico),
            VALOR: Math.abs(valor),
            SALDO: 0,
            IDENTIFICADOR_EXTERNO: '',
            VALOR_CONCILIADO: 0,
            VALOR_PENDENTE: Math.abs(valor),
            STATUS: 'PENDENTE'
          })
        } catch (err) {
          console.warn('Erro ao parsear linha:', linha, err)
          continue
        }
      }
      
      return itens
    },

    converterDataBR(dataStr) {
      // Formato esperado: dd/mm/aaaa
      const match = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (!match) return null
      
      const dia = match[1]
      const mes = match[2]
      const ano = match[3]
      
      return `${ano}-${mes}-${dia}`
    },

    converterValorBR(valorStr) {
      // Remove espaços
      let valor = valorStr.trim()
      
      // Remove separadores de milhares (pontos)
      valor = valor.replace(/\./g, '')
      
      // Substitui vírgula decimal por ponto
      valor = valor.replace(',', '.')
      
      return parseFloat(valor)
    },

    inferirForma(historico) {
      const hist = historico.toUpperCase()
      if (hist.includes('PIX')) return 'PIX'
      if (hist.includes('BOLETO')) return 'BOLETO'
      if (hist.includes('TED')) return 'TED'
      if (hist.includes('DOC')) return 'DOC'
      if (hist.includes('TRANSFERENCIA')) return 'TRANSFERENCIA'
      if (hist.includes('DEBITO')) return 'DEBITO_AUTOMATICO'
      return 'OUTROS'
    },

    async selecionarItem(item) {
      if (this.abrindoModalItem) return

      this.abrindoModalItem = true
      this.$store.commit('controle/setAguardando', true)

      try {
        this.itemSelecionado = item
        this.$store.commit('conciliacaoBancaria/SET_ITEM_SELECIONADO', item)

        await Promise.all([
          this.carregarLancamentos(),
          this.carregarVinculos()
        ])

        this.$refs.modalVinculacao.show()
      } catch (err) {
        console.error('Erro ao preparar modal de vínculo', err)
        this.$bvToast.toast('Erro ao preparar modal de vínculo', { variant: 'danger' })
      } finally {
        this.$store.commit('controle/setAguardando', false)
        this.abrindoModalItem = false
      }
    },

    fecharModal() {
      this.$refs.modalVinculacao.hide()
    },

    onModalShown() {
      // Dados já são carregados antes da abertura do modal.
    },

    onModalHidden() {
      // Limpar seleções ao fechar
      this.lancamentosSelecionados = []
      this.valoresVinculoLancamentos = {}
      this.mostrarConclusaoManual = false
      this.motivoConclusaoManual = ''
      this.fecharModalGerarLancamento()
    },

    async carregarTiposPagamentoLancamento() {
      const response = await this.$http.get('/api/protected/tipopagamento/listartodos')
      const tipoPagamentos = Array.isArray(response.data) ? response.data : []
      this.lancamentoGerado.tipoPagamento.items = tipoPagamentos.map(item => ({
        value: item.CODIGONOME,
        text: item.CODIGONOME
      }))
    },

    resetarLancamentoGerado() {
      const item = this.itemSelecionado || {}
      this.lancamentoGerado.parametros.submmited = false
      this.lancamentoGerado.parametros.overlay = false
      this.lancamentoGerado.registro = {
        CONTADOR: null,
        SEQUENCIA: null,
        COD_CONTA: this.registro.CONTA_ID || null,
        DOCUMENTO: item.DOCUMENTO || null,
        COD_BANCO: this.registro.BANCO_ID || this.registro.COD_BANCO || null,
        COD_PLANO: null,
        OCORRENCIA: 'SEM OCORRENCIA',
        TIPO_MOV: item.TIPO_MOV === 'C' ? 'CREDITO' : 'DEBITO',
        DT_MOV: this.normalizarDataParam(item.DATA_MOVIMENTO),
        VL_MOV: Number(item.VALOR_PENDENTE || 0),
        FORMA: null,
        CONTROL_CHEQUE: null,
        MARCA: null,
        OBS: item.HISTORICO || null,
        COD_CENTRO1: null,
        HORA: null,
        PARCELA: null,
        CLIENTE_ID: null
      }
      this.gerarLancamentoClienteKey += 1
    },

    async abrirModalGerarLancamento() {
      if (!this.podeGerarLancamento) return

      try {
        this.$store.commit('controle/setAguardando', true)
        this.resetarLancamentoGerado()
        await this.carregarTiposPagamentoLancamento()
        this.$refs.modalGerarLancamento.show()
      } catch (err) {
        console.error('Erro ao abrir modal de geração de lançamento', err)
        this.$bvToast.toast(err.response?.data || 'Erro ao preparar geração de lançamento', { variant: 'danger' })
      } finally {
        this.$store.commit('controle/setAguardando', false)
      }
    },

    fecharModalGerarLancamento() {
      if (this.$refs.modalGerarLancamento) {
        this.$refs.modalGerarLancamento.hide()
      }
    },

    onModalGerarLancamentoHidden() {
      this.resetarLancamentoGerado()
    },

    async confirmarGeracaoLancamento() {
      if (!this.itemSelecionado || !this.itemSelecionado.ID) return

      this.lancamentoGerado.parametros.submmited = true
      if (!this.validouGerarLancamento) {
        this.$bvToast.toast('Preencha os campos obrigatórios do lançamento', { variant: 'warning' })
        return
      }

      try {
        this.lancamentoGerado.parametros.overlay = true

        const body = {
          conciliacaoItemId: this.itemSelecionado.ID,
          registro: { ...this.lancamentoGerado.registro },
          login: window.localStorage.getItem('login') || 'sistema'
        }

        await this.$http.post('/api/protected/conciliacao-bancaria/gerar-lancamento', body)

        this.$bvToast.toast('Lançamento gerado e vinculado com sucesso.', { variant: 'success' })
        this.fecharModalGerarLancamento()
        await this.atualizarAposVinculo()
        await this.carregarLancamentos()
      } catch (err) {
        console.error('Erro ao gerar lançamento', err)
        this.$bvToast.toast(err.response?.data || 'Erro ao gerar lançamento', { variant: 'danger' })
      } finally {
        this.lancamentoGerado.parametros.overlay = false
      }
    },

    rowClassItem(item, type) {
      if (type !== 'row' || !item) return ''
      return this.itemSelecionado && item.ID === this.itemSelecionado.ID ? 'table-active' : ''
    },

    rowAttrsItem(item) {
      if (!item) return {}
      return {
        id: `item-row-${item.ID}`,
        style: this.abrindoModalItem ? 'pointer-events: none; opacity: 0.65;' : 'cursor: pointer;'
      }
    },

    async carregarLancamentos() {
      if (!this.itemSelecionado) return

      if (String(this.itemSelecionado.STATUS || '') === 'CONCILIADO_MANUAL') {
        this.$store.commit('conciliacaoBancaria/SET_CANDIDATOS_RBANMOV1', [])
        return
      }
      
      try {
        const params = {
          contaId: this.registro.CONTA_ID,
          dataInicial: this.normalizarDataParam(this.registro.DATA_INICIAL),
          dataFinal: this.normalizarDataParam(this.registro.DATA_FINAL),
          tipoMov: this.itemSelecionado.TIPO_MOV
        }
        await this.$store.dispatch('conciliacaoBancaria/GET_CANDIDATOS_RBANMOV1', params)
      } catch (err) {
        console.error('Erro ao carregar lançamentos', err)
        this.$bvToast.toast('Erro ao carregar lançamentos', { variant: 'danger' })
      }
    },

    async carregarVinculos() {
      if (!this.itemSelecionado) return
      
      try {
        await this.$store.dispatch('conciliacaoBancaria/GET_VINCULOS', this.itemSelecionado.ID)
      } catch (err) {
        console.error('Erro ao carregar vínculos', err)
      }
    },

    onLancamentosSelecionados(items) {
      this.lancamentosSelecionados = items
      const selecionados = {}
      items.forEach(item => {
        const valorAtual = Number(this.valoresVinculoLancamentos[item.ID])
        const valorDisponivel = Number(item.VALOR_DISPONIVEL || 0)
        selecionados[item.ID] = Number.isFinite(valorAtual) && valorAtual > 0
          ? Math.min(valorAtual, valorDisponivel)
          : Math.min(valorDisponivel, Number(this.itemSelecionado?.VALOR_PENDENTE || 0))
      })
      this.valoresVinculoLancamentos = selecionados
    },

    ajustarValorVincular(lancamento) {
      const valorDisponivel = Number(lancamento.VALOR_DISPONIVEL || 0)
      let valor = Number(this.valoresVinculoLancamentos[lancamento.ID] || 0)
      if (!Number.isFinite(valor) || valor < 0) valor = 0
      if (valor > valorDisponivel) valor = valorDisponivel
      this.$set(this.valoresVinculoLancamentos, lancamento.ID, valor)
    },

    async vincularLancamentos() {
      if (this.diferencaLancamentos > 0.009) {
        this.$bvToast.toast('A soma dos valores para vincular deve ser igual ao valor pendente do item', { variant: 'warning' })
        return
      }

      try {
        const vinculos = this.lancamentosSelecionados
          .map(lanc => ({
          TIPO_ORIGEM: 'RBANMOV1',
          RBANMOV1_ID: lanc.ID,
          VALOR_VINCULADO: Number(this.valoresVinculoLancamentos[lanc.ID] || 0)
          }))
          .filter(v => v.VALOR_VINCULADO > 0)

        if (vinculos.length === 0) {
          this.$bvToast.toast('Nenhum valor válido informado para vínculo', { variant: 'warning' })
          return
        }

        const body = {
          conciliacaoItemId: this.itemSelecionado.ID,
          vinculos: vinculos,
          login: window.localStorage.getItem('login') || 'sistema'
        }

        await this.$http.post('/api/protected/conciliacao-bancaria/criar-vinculo', body)
        
        this.$bvToast.toast('Vínculos criados com sucesso', { variant: 'success' })
        this.lancamentosSelecionados = []
        this.valoresVinculoLancamentos = {}
        await this.atualizarAposVinculo()
      } catch (err) {
        console.error('Erro ao vincular', err)
        this.$bvToast.toast(err.response?.data || 'Erro ao criar vínculos', { variant: 'danger' })
      }
    },

    async desfazerVinculo(vinculo) {
      const confirmar = await this.$bvModal.msgBoxConfirm('Deseja realmente desfazer este vínculo?', {
        title: 'Confirmar',
        okTitle: 'Sim',
        cancelTitle: 'Não',
        centered: true
      })

      if (!confirmar) return

      try {
        const body = {
          vinculoId: vinculo.ID,
          login: window.localStorage.getItem('login') || 'sistema'
        }

        await this.$http.post('/api/protected/conciliacao-bancaria/excluir-vinculo', body)
        
        this.$bvToast.toast('Vínculo desfeito com sucesso', { variant: 'success' })
        await this.atualizarAposVinculo()
      } catch (err) {
        console.error('Erro ao desfazer vínculo', err)
        this.$bvToast.toast(err.response?.data || 'Erro ao desfazer vínculo', { variant: 'danger' })
      }
    },

    async atualizarAposVinculo() {
      // Guardar o ID do item selecionado antes de recarregar
      const itemIdSelecionado = this.itemSelecionado?.ID

      // Recarregar dados da conciliação e itens
      await this.carregar(this.registro.ID)

      // Aguardar o próximo tick do Vue para garantir que o DOM foi atualizado
      await this.$nextTick()

      // Se tinha item selecionado, encontrar e selecionar novamente
      if (itemIdSelecionado) {
        const itemAtualizado = this.itensConciliacao.find(i => i.ID === itemIdSelecionado)
        if (itemAtualizado) {
          // Reselecionar o item
          this.itemSelecionado = itemAtualizado
          this.$store.commit('conciliacaoBancaria/SET_ITEM_SELECIONADO', itemAtualizado)

          // Recarregar vínculos do item
          await this.carregarVinculos()

          // Fazer scroll até o item selecionado
          await this.$nextTick()
          const elemento = document.getElementById(`item-row-${itemIdSelecionado}`)
          if (elemento) {
            elemento.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      }
    },

    async confirmarConclusaoManual() {
      if (!this.itemSelecionado || !this.itemSelecionado.ID) return

      const motivo = String(this.motivoConclusaoManual || '').trim()
      if (!motivo) {
        this.$bvToast.toast('Informe o motivo para concluir manualmente', { variant: 'warning' })
        return
      }

      const confirmar = await this.$bvModal.msgBoxConfirm(
        `${this.mensagemConclusaoManual}\n\nDiferença: ${this.formatarMoeda(this.diferencaConclusaoManual)}\nTolerância: ${this.formatarMoeda(this.toleranciaContaAtual)}\n\nDeseja confirmar a conclusão manual?`,
        {
          title: 'Concluir item manualmente',
          okTitle: 'Confirmar',
          cancelTitle: 'Cancelar',
          centered: true
        }
      )

      if (!confirmar) return

      try {
        const body = {
          conciliacaoItemId: this.itemSelecionado.ID,
          motivo,
          login: window.localStorage.getItem('login') || 'sistema'
        }

        const { data } = await this.$http.post('/api/protected/conciliacao-bancaria/concluir-manualmente', body)

        this.$bvToast.toast(
          data?.acimaTolerancia
            ? 'Item concluído manualmente com ajuste acima da tolerância.'
            : 'Item concluído manualmente com sucesso.',
          { variant: data?.acimaTolerancia ? 'warning' : 'success' }
        )

        this.mostrarConclusaoManual = false
        this.motivoConclusaoManual = ''
        await this.atualizarAposVinculo()
        await this.carregarLancamentos()
      } catch (err) {
        console.error('Erro ao concluir manualmente', err)
        this.$bvToast.toast(err.response?.data || 'Erro ao concluir manualmente', { variant: 'danger' })
      }
    },

    getVariantStatus(status) {
      return getVariantStatusConciliacao(status)
    },

    getLabelStatus(status) {
      return getLabelStatusConciliacao(status)
    },

    getVariantStatusItem(status) {
      const variants = {
        'PENDENTE': 'warning',
        'CONCILIADO_PARCIAL': 'info',
        'CONCILIADO': 'success',
        'CONCILIADO_MANUAL': 'primary'
      }
      return variants[status] || 'secondary'
    },

    getLabelStatusItem(status) {
      const labels = {
        'PENDENTE': 'Pendente',
        'CONCILIADO_PARCIAL': 'Parcial',
        'CONCILIADO': 'Conciliado',
        'CONCILIADO_MANUAL': 'Conciliado manualmente'
      }
      return labels[status] || status
    },

    formatarDataBR(data) {
      if (!data) return ''
      try {
        const d = new Date(data)
        return d.toLocaleDateString('pt-BR')
      } catch {
        return ''
      }
    },

    formatarDataHoraBR(data) {
      if (!data) return '-'
      try {
        const d = new Date(data)
        return d.toLocaleString('pt-BR')
      } catch {
        return '-'
      }
    },

    formatarMoeda(valor) {
      if (!valor && valor !== 0) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', { 
        style: 'currency', 
        currency: 'BRL' 
      }).format(valor)
    },

    normalizarDataParam(valor) {
      if (!valor) return valor
      const texto = String(valor).trim()
      return texto.length >= 10 ? texto.substring(0, 10) : texto
    }
  },

  async created() {
    const id = this.$route.params.id
    if (id) {
      await this.carregar(id)
    }
  }
}
</script>

<style scoped>
.table-active {
  background-color: rgba(0, 123, 255, 0.1) !important;
}

.modal-header-premium {
  border-bottom: none;
}

.header-icon-wrapper {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1.5rem;
}

.bg-white-10 {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.modal-label-premium {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin-bottom: 0.4rem;
  display: block;
}

.shadow-xs {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.modal-info-bar {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1.25rem;
}

.modal-field-group {
  margin-bottom: 1.2rem;
}

.modal-section-title {
  font-weight: 800;
  color: #2c3e50;
  border-bottom: 2px solid #edf2f7;
  padding-bottom: 0.4rem;
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modal-table-premium {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.modal-footer-premium {
  border-top: 1px solid #dee2e6;
}
</style>



// WEBPACK FOOTER //
// src/views/financeiro/conciliacao-bancaria/ConciliacaoBancariaCadastro.vue